import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { imageService } from '@bedrock/fido-sdk';
import {
  DEFAULT_IMAGE_FORMAT,
  TRANSPARENT_IMAGE_FORMAT,
  IMAGE_FIT_SCALE_CROP,
  IMAGE_FIT_MAX,
  IMAGE_QUALITY,
} from '@bedrock-rdk/constants';
import { getNextIndex } from '../../logic/screenSaver/screenSaver.utils';
import { BrandIcon } from '../tornado/brand-icon/brandIcon.component';
import { cover } from '../tornado/utils/textures';
import connect from './screenSaver.connector';
import {
  BACKGROUND_STYLES,
  getFadeAnimStyle,
  getBackgroundIdleAnimStyle,
  getDescriptionAppearAnimStyle,
  getInitialStyle,
  getDummyAnimStyle,
  getProgramLogoStyle,
} from './screenSaver.style';
import {
  ITERATION_TOTAL_DURATION,
  SCREEN_APPEAR_DURATION,
  BACKGROUND_DURATION,
  FADE_IN,
  FADE_OUT,
  LABEL_FADE_IN_DURATION,
  LABEL_FADE_OUT_DURATION,
  LABEL_SWAP_DURATION_OFFSET,
  LOGO_APPEAR_DURATION,
  BACKGROUND_FADE_DELAY,
  BACKGROUND_FADE_IN_DURATION,
  LABEL_APPEAR_DELAY,
  BACKGROUND_FADE_OUT_FAST,
  HIDE_SCREENSAVER_SIGNAL,
  SCREEN_SAVER_COUNTDOWN,
  SHOW_SCREENSAVER_SIGNAL,
} from './screenSaver.constants';

export default class ScreenSaver extends Lightning.Component {
  static _template() {
    return _merge(getInitialStyle(), {
      rect: true,
      Logo: {
        type: BrandIcon,
      },
    });
  }

  _build() {
    this._index = 0;
    this._container = this.tag('BackgroundContainer');

    this._logoContainer = this.tag('LogoContainer');

    this._label = this.tag('Label');

    this._label.patch({
      appear: this._label.animation(getDescriptionAppearAnimStyle()),
      fadeIn: this._label.animation(getFadeAnimStyle(FADE_IN, LABEL_FADE_IN_DURATION, LABEL_APPEAR_DELAY)),
      fadeOut: this._label.animation(getFadeAnimStyle(FADE_OUT, LABEL_FADE_OUT_DURATION)),
    });

    this._appearAnimation = this.animation(getFadeAnimStyle(FADE_IN, SCREEN_APPEAR_DURATION));
    this._logoAppearAnimation = this.tag('Logo').animation(getFadeAnimStyle(FADE_IN, LOGO_APPEAR_DURATION));

    this._labelSwapTimer = this.animation(getDummyAnimStyle(LABEL_SWAP_DURATION_OFFSET));
    this._backgroundSwapTimer = this.animation(getDummyAnimStyle(BACKGROUND_DURATION));
    this._logoSwapTimer = this.animation(getDummyAnimStyle(BACKGROUND_DURATION));
    this._iterationTimer = this.animation(getDummyAnimStyle(ITERATION_TOTAL_DURATION));

    this._labelSwapTimer.on('finish', () => this._onLabelSwapTimerFinished());
    this._backgroundSwapTimer.on('finish', () => this._onBackgroundSwapTimerFinished());
    this._logoSwapTimer.on('finish', () => this._onLogoSwapTimerFinished());
    this._iterationTimer.on('finish', () => this._onIterationTimerFinished());

    this._label.fadeOut.on('finish', () => this._onLabelFadeOutFinished());

    this._isLocked = false;
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }

    this._reset();
  }

  set items(value) {
    const { finalW, finalH } = this;
    this._items = value;

    if (this._items.length === 1) {
      for (let i = 0; i < BACKGROUND_STYLES.length - 1; i += 1) {
        this._items.push(this._items[0]);
      }
    }

    let backgroundStyleIndex = -1;
    this._container.children = this._items.map(() => {
      backgroundStyleIndex = getNextIndex(backgroundStyleIndex, BACKGROUND_STYLES);

      return BACKGROUND_STYLES[backgroundStyleIndex];
    });

    const { w: logoWidth, h: logoHeight } = getProgramLogoStyle(this._style);
    this._logoContainer.children = this._items.map(() => ({}));
    this._logoContainer.children.forEach((item, index) => {
      item.patch({
        alpha: 0,
        fadeIn: item.animation(getFadeAnimStyle(FADE_IN, BACKGROUND_FADE_IN_DURATION, BACKGROUND_FADE_DELAY)),
        fadeOut: item.animation(getFadeAnimStyle(FADE_OUT, BACKGROUND_FADE_OUT_FAST)),
        texture: {
          src: imageService.getUrl(this._items[index].logoId, logoWidth, logoHeight, {
            fit: IMAGE_FIT_MAX,
            quality: IMAGE_QUALITY,
            format: TRANSPARENT_IMAGE_FORMAT,
          }),
          type: Lightning.textures.ImageTexture,
          hasAlpha: true,
        },
      });
    });

    this._container.children.forEach((item, index) => {
      item.patch({
        alpha: 0,
        fadeIn: item.animation(getFadeAnimStyle(FADE_IN, BACKGROUND_FADE_IN_DURATION, BACKGROUND_FADE_DELAY)),
        fadeOut: item.animation(getFadeAnimStyle(FADE_OUT, BACKGROUND_FADE_OUT_FAST)),
        idle: item.animation(getBackgroundIdleAnimStyle(item.zoom)),
        texture: cover(
          imageService.getUrl(this._items[index].imageId, finalW, finalH, {
            fit: IMAGE_FIT_SCALE_CROP,
            quality: IMAGE_QUALITY,
            format: DEFAULT_IMAGE_FORMAT,
          }),
          finalW,
          finalH,
        ),
      });
    });

    if (this.canBeShown) {
      this.resetTimer();
    }
  }

  get canBeShown() {
    // NOTE: the "visible" flag could be set to false by Router in case if ScreenSaver is not presented in a "widgets" list for a current route
    return this.visible && !this._isLocked && Boolean(this._items && this._items.length);
  }

  get isShown() {
    return this.alpha > 0;
  }

  set isLocked(value) {
    this._isLocked = value;
  }

  resetTimer() {
    if (this._screenSaverTimeout) {
      clearTimeout(this._screenSaverTimeout);
    }

    if (this.isShown) {
      return;
    }

    this._screenSaverTimeout = setTimeout(() => {
      this.signal(SHOW_SCREENSAVER_SIGNAL);
    }, SCREEN_SAVER_COUNTDOWN);
  }

  _handleKey() {
    this.signal(HIDE_SCREENSAVER_SIGNAL);
    this.resetTimer();
  }

  _focus() {
    this._appearAnimation.start();
    this._startSequence();
  }

  _unfocus() {
    this._reset();
  }

  _startSequence() {
    this._container.children.forEach(item => {
      item.alpha = 0;
    });
    this._logoContainer.children.forEach(item => {
      item.alpha = 0;
    });
    this._logoAppearAnimation.start();
    this._startBackgroundAnimation(0, true);
    this._startLogoAnimation(0, true);
    this._startLabelAnimation(0, true);
    this._processSequence();
  }

  _processSequence() {
    this._labelSwapTimer.start();
    this._backgroundSwapTimer.start();
    this._logoSwapTimer.start();
    this._iterationTimer.start();
  }

  _onLabelSwapTimerFinished() {
    if (this.isShown) {
      this._label.fadeOut.start();
    }
  }

  _onBackgroundSwapTimerFinished() {
    if (this.isShown) {
      this._container.children[this._index].fadeOut.start();
      this._startBackgroundAnimation(getNextIndex(this._index, this._items));
    }
  }

  _onLogoSwapTimerFinished() {
    if (this.isShown) {
      this._logoContainer.children[this._index].fadeOut.start();
      this._startLogoAnimation(getNextIndex(this._index, this._items));
    }
  }

  _onIterationTimerFinished() {
    if (this.isShown) {
      this._index = getNextIndex(this._index, this._items);
      this._processSequence();
    }
  }

  _onLabelFadeOutFinished() {
    if (this.isShown) {
      this._startLabelAnimation(getNextIndex(this._index, this._items));
    }
  }

  _startLabelAnimation(index, skipDelay = false) {
    const currentItem = this._items[index];
    this.tag('Label').patch({
      Title: {
        text: {
          text: currentItem.title,
        },
      },
      Details: {
        text: {
          text: currentItem.details,
        },
      },
    });

    this._label.appear.start();
    this._label.fadeIn.start();
    if (skipDelay) {
      this._label.appear.skipDelay();
      this._label.fadeIn.skipDelay();
    }
  }

  _startBackgroundAnimation(index, skipDelay = false) {
    this._container.children[index].fadeIn.start();
    if (skipDelay) {
      this._container.children[index].fadeIn.skipDelay();
    }
    this._container.children[index].idle.start();
  }

  _startLogoAnimation(index, skipDelay = false) {
    this._logoContainer.children[index].fadeIn.start();
    if (skipDelay) {
      this._logoContainer.children[index].fadeIn.skipDelay();
    }
  }

  _reset() {
    if (this._screenSaverTimeout) {
      clearTimeout(this._screenSaverTimeout);
    }

    this._container.children.forEach(item => {
      item.fadeIn.stopNow();
      item.fadeOut.stopNow();
      item.idle.stopNow();
    });

    this._label.appear.stopNow();
    this._label.fadeIn.stopNow();
    this._label.fadeOut.stopNow();
    this._appearAnimation.stopNow();
    this._logoAppearAnimation.stopNow();
    this._labelSwapTimer.stopNow();
    this._backgroundSwapTimer.stopNow();
    this._iterationTimer.stopNow();
    this._logoContainer.children.forEach(item => {
      item.fadeIn.stopNow();
      item.fadeOut.stopNow();
    });

    this._index = 0;
    this.alpha = 0;
  }
}
